/**
 * errorMessage.js — safe extractor for API error strings.
 *
 * FastAPI/Pydantic v2 returns validation errors as:
 *   { detail: [{ type, loc, msg, input, ctx, url }, ...] }
 *
 * Legacy 4xx/5xx typically return:
 *   { detail: "Human string" }  OR  { detail: { error, message, upgrade_url } }
 *
 * Naïve callers doing `toast.error(err.response?.data?.detail || 'fallback')`
 * crash React with:
 *   "Objects are not valid as a React child
 *    (found: object with keys {type, loc, msg, input, ctx})."
 *
 * This helper always returns a **plain string** suitable for toasts, JSX
 * children, alert()-like UIs. Never returns objects/arrays.
 */

/**
 * Turn a single Pydantic error entry into a readable Danish string.
 * Best-effort: falls back to the raw `msg` if the type isn't recognised.
 */
const pydanticEntryToString = (entry) => {
  if (!entry || typeof entry !== 'object') return String(entry ?? '');
  const msg = typeof entry.msg === 'string' ? entry.msg : '';
  const field = Array.isArray(entry.loc)
    ? entry.loc.filter((l) => l !== 'body').join('.')
    : '';
  if (field && msg) return `${field}: ${msg}`;
  return msg || entry.type || 'Valideringsfejl';
};

/**
 * Extract a user-visible string from an axios error's response body.
 * @param {*} err     Axios error (or anything).
 * @param {string} fallback  What to return if nothing usable is found.
 * @returns {string}  Always a string.
 */
export function getErrorMessage(err, fallback = 'Der opstod en fejl. Prøv igen.') {
  if (!err) return fallback;

  const detail = err?.response?.data?.detail;

  // 1) String detail — happy path.
  if (typeof detail === 'string' && detail.trim()) return detail;

  // 2) Pydantic v2 list of error entries.
  if (Array.isArray(detail) && detail.length > 0) {
    const parts = detail
      .map(pydanticEntryToString)
      .filter((s) => s && s.length > 0);
    if (parts.length > 0) return parts.join(' · ');
  }

  // 3) Object detail (e.g. structured business errors like the AI-gate).
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    if (typeof detail.message === 'string' && detail.message.trim()) {
      return detail.message;
    }
    if (typeof detail.error === 'string' && detail.error.trim()) {
      return detail.error;
    }
  }

  // 4) Fall back to error's own message / string form.
  if (typeof err.message === 'string' && err.message.trim()) {
    return err.message;
  }
  return fallback;
}

export default getErrorMessage;
